import React, { useRef, useEffect, useMemo } from 'react'
import ApexCharts from 'apexcharts'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import { CHANNEL_COLORS, CHANNEL_LABELS } from './channelColors'

const COST_CHANNELS = ['whatsapp', 'sms', 'calls_futwork', 'calls_futworkAI']

export default function CostBreakdownChart({ data = [], title = 'Cost Trend by Channel', height = 340, loading }) {
  /* data shape: [{ date: '2026-06-01', whatsapp: 1920, sms: 480, calls_futwork: 18900, calls_futworkAI: 0 }] */

  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  const activeChannels = useMemo(() =>
    COST_CHANNELS.filter(ch => data.some(d => (d[ch] || 0) > 0)),
    [data]
  )

  const options = useMemo(() => ({
    chart: { type: 'area', height, stacked: true, toolbar: { show: false }, fontFamily: "'Inter', sans-serif" },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 1.5 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 100] } },
    colors: activeChannels.map(ch => CHANNEL_COLORS[ch]),
    xaxis: {
      categories: data.map(d => d.date),
      labels: { style: { fontSize: '11px' } },
    },
    yaxis: {
      labels: {
        formatter: (v) => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(1)}K` : `₹${Math.round(v)}`,
        style: { fontSize: '11px' },
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      fontSize: '12px',
      fontWeight: 600,
    },
    tooltip: {
      y: { formatter: (v) => `₹${v?.toLocaleString('en-IN')}` },
    },
    grid: { borderColor: '#E0E4E8', strokeDashArray: 4 },
  }), [data, activeChannels, height])

  const chartSeries = useMemo(() =>
    activeChannels.map(ch => ({
      name: CHANNEL_LABELS[ch],
      data: data.map(d => d[ch] || 0),
    })),
    [data, activeChannels]
  )

  useEffect(() => {
    if (!chartRef.current) return
    if (chartInstance.current) chartInstance.current.destroy()
    chartInstance.current = new ApexCharts(chartRef.current, { ...options, series: chartSeries })
    chartInstance.current.render()
    return () => { if (chartInstance.current) { chartInstance.current.destroy(); chartInstance.current = null } }
  }, [options, chartSeries])

  if (loading) {
    return (
      <Paper sx={{ p: 2.5 }}>
        <Skeleton width={200} height={22} sx={{ mb: 2 }} />
        <Skeleton variant='rectangular' height={height - 60} sx={{ borderRadius: 1 }} />
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant='subtitle1' sx={{ mb: 1 }}>{title}</Typography>
      <div ref={chartRef} />
    </Paper>
  )
}
